import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, MessageCircle, MoreHorizontal, Phone } from 'lucide-react';
import { isOpenTask } from '@tailoring-crm/shared';
import { NotesThread } from '../components/activity/notes-thread.js';
import { CustomerCallsTab } from '../components/customers/customer-calls-tab.js';
import { CustomerFollowUpsTab } from '../components/customers/customer-follow-ups-tab.js';
import { CustomerOrders } from '../components/customers/customer-orders.js';
import { CustomerTimeline } from '../components/customers/customer-timeline.js';
import { MergeCustomerDialog } from '../components/customers/merge-customer-dialog.js';
import { StaffAssigneeSelect } from '../components/customers/staff-assignee-select.js';
import { DesignBoardPanel } from '../components/design-board/design-board-panel.js';
import { PageHeader } from '../components/layout/page-header.js';
import { MeasurementsPanel } from '../components/measurements/measurements-panel.js';
import { QuotationCard } from '../components/quotations/quotation-card.js';
import {
  Badge,
  Button,
  useConfirm,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ListSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import { useTasks } from '../lib/activity/use-activity.js';
import { useAuth } from '../lib/auth/auth-context.js';
import {
  useCustomer,
  useCustomerTimeline,
  useDeleteCustomer,
  useUpdateCustomer,
} from '../lib/customers/use-customers.js';
import { formatCurrency, formatDate, formatDateLong, formatMobile } from '../lib/format.js';

/**
 * One customer, and everything the shop has ever done for them.
 *
 * Two panes, with one rule about which side a thing lands on: LEFT is what this person IS (the
 * number, the figures, the fields), RIGHT is what has HAPPENED and what is being DONE (their
 * orders, what is being sold to them, their measurements, what has been written down).
 *
 * It replaces a dialog. A dialog was the wrong container the moment this screen grew
 * measurements AND an order history: three working surfaces, each wanting the
 * full width, stacked inside a box that had to be scrolled and could not be linked to. A
 * customer now has an address of their own — `/customers/:customerId` — which is what the
 * board and the measurements band point at.
 *
 * The contact actions stay the loudest thing on the pane, because `tel:` and `wa.me` hand off
 * to the dialler and to WhatsApp on the phone this app runs on — which is how a shop chases a
 * delivery, and the single most-used thing here.
 */
/**
 * The right pane's vocabulary, in the order it reads.
 *
 * The customer is the only contact record — somebody who has not ordered yet is simply a customer
 * with no orders — so everything the shop does to win and keep somebody lives here: what was
 * written down (Notes), who rang whom (Calls), when to get in touch next (Follow-ups), what they
 * were quoted (Quotation), beside their orders, measurements and designs.
 *
 * The timeline is the CUSTOMER'S — everything the shop did for them, notes and calls included.
 * Quotation is money, so a tailor is never offered it; every other tab is always there.
 *
 * A tab is added here and nowhere else — the strip, the state type and the translation key all
 * derive from this list, so a new pane cannot be half-added.
 */
const CUSTOMER_TABS = [
  'timeline',
  'notes',
  'calls',
  'followUps',
  'quotation',
  'orders',
  'measurements',
  'designs',
] as const;
type CustomerTab = (typeof CUSTOMER_TABS)[number];

export function CustomerDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { customerId = '' } = useParams();

  const { role } = useAuth();
  /** Money, and the permission matrix refuses a tailor every quotation route. */
  const canSeeQuotations = role !== 'tailor';

  const customerQuery = useCustomer(customerId || null);
  const remove = useDeleteCustomer();
  const update = useUpdateCustomer(customerId);
  // For the tab's badge: open only — a badge that counted history would read `47` forever.
  const tasks = useTasks(customerId ? { kind: 'customer', id: customerId } : null);

  /**
   * Null until somebody picks, which is what lets the strip open on the timeline by itself.
   * A tab that is not offered (a tailor's `?tab=quotation`) falls back to the timeline rather
   * than rendering nothing.
   *
   * `?tab=` SEEDS IT, ONCE, AND IS NEVER WRITTEN BACK. Links arrive here aimed at a particular
   * pane, and landing
   * on the timeline instead would make the link a lie. It is a lazy initial value rather than a
   * derived one on purpose: once somebody is on the page, the tab strip is theirs, and a URL that
   * rewrote itself on every click would fill the back button with panes rather than pages.
   */
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<CustomerTab | null>(() => {
    const requested = searchParams.get('tab');
    return requested && (CUSTOMER_TABS as readonly string[]).includes(requested)
      ? (requested as CustomerTab)
      : null;
  });
  const [merging, setMerging] = useState(false);

  /**
   * Which pane — settled before the early returns below, because the timeline query is a hook and
   * cannot be asked for from inside a branch.
   */
  const tabs = CUSTOMER_TABS.filter((one) => one !== 'quotation' || canSeeQuotations);
  /**
   * What the pane opens on when nobody has chosen: the timeline, for everybody — the answer to
   * "what has been going on with this person". A walk-in has a history too; it is their orders,
   * their payments and their fittings.
   */
  const activeTab: CustomerTab = tab && tabs.includes(tab) ? tab : 'timeline';

  // Only the tab that draws it asks for it; a customer whose history nobody opens costs nothing.
  const timeline = useCustomerTimeline(customerId && activeTab === 'timeline' ? customerId : null);

  if (customerQuery.isPending) return <ListSkeleton rows={6} />;

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('customers.notFound')}
        </p>
      </div>
    );
  }

  const customer = customerQuery.data;

  const tabCounts: Record<CustomerTab, number> = {
    timeline: 0,
    notes: 0,
    calls: 0,
    followUps: (tasks.data?.items ?? []).filter((task) => isOpenTask(task.status)).length,
    quotation: 0,
    orders: customer.orderCount,
    measurements: 0,
    designs: 0,
  };

  const onDelete = (): void => {
    void (async () => {
      const confirmed = await confirm({
        title: t('customers.deleteTitle', { name: customer.name }),
        description: t('customers.deleteDescription'),
        confirmLabel: t('customers.deleteConfirm'),
        destructive: true,
      });
      if (!confirmed) return;

      await remove.mutateAsync(customer.id);
      toast({ title: t('customers.deleted', { name: customer.name }), variant: 'success' });
      navigate('/customers');
    })();
  };

  /** Handing the customer to somebody else, or to nobody — saved the moment it is picked. */
  const onAssign = (userId: string): void => {
    update.mutate(
      { assignedToUserId: userId === '' ? null : userId },
      {
        onSuccess: () => toast({ title: t('customers.assignedSaved'), variant: 'success' }),
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  const address = [customer.address.line1, customer.address.line2, customer.address.city]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="customer-dossier flex flex-col gap-6">
      <BackLink />

      <PageHeader
        title={customer.name}
        description={t('customers.detail.customerSince', {
          date: formatDateLong(customer.createdAt),
        })}
        className="dossier-header"
        action={
          <>
            {/* Edit is the only labelled button. Removing a customer is not an edit — it takes
                their order history's owner away with it — so it keeps a menu of its own rather
                than sitting on this line where a thumb can find it by accident. */}
            {/* The edit form is a page of its own now: the record carries a photograph, a
                measurement history and an order history, none of which fit in a modal. */}
            <Button asChild>
              <Link to={`/customers/${customer.id}/edit`}>{t('customers.detail.edit')}</Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={remove.isPending}
                  aria-label={t('customers.moreActions')}
                >
                  <MoreHorizontal aria-hidden="true" className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Folding a stray second record into this one. Above Remove, because it is
                    what somebody who has just found a duplicate almost always wants instead. */}
                <DropdownMenuItem onSelect={() => setMerging(true)}>
                  {t('customers.merge.action')}
                </DropdownMenuItem>
                <DropdownMenuItem destructive onSelect={onDelete}>
                  {t('customers.detail.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <MergeCustomerDialog open={merging} onOpenChange={setMerging} survivor={customer} />

      <div className="dossier-split">
        <aside className="dossier-aside flex flex-col gap-6">
          {/* What this person is worth to the shop, and whether anything is live on them.
              Answered before anything else is read. */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">{t('customers.detail.statsHeading')}</h3>
            <dl className="flex flex-col gap-1.5 text-sm">
              <StatRow
                dotClassName="bg-primary"
                label={t('customers.detail.orders')}
                value={String(customer.orderCount)}
              />
              <StatRow
                dotClassName="bg-emerald-500"
                label={t('customers.detail.lifetimeValue')}
                value={formatCurrency(customer.lifetimeValuePaise, { compact: true })}
              />
              <StatRow
                dotClassName="bg-muted-foreground"
                label={t('customers.detail.lastOrder')}
                value={
                  customer.lastOrderAt
                    ? formatDate(customer.lastOrderAt)
                    : t('customers.neverOrdered')
                }
              />
            </dl>
          </section>

          {/* The dialler and WhatsApp, full width and first: this is what somebody opened the
              screen to do. */}
          <section className="dossier-contact flex flex-col gap-3 rounded-lg border border-border p-4">
            {customer.tags.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5">
                {customer.tags.map((tag) => (
                  <li key={tag}>
                    <Badge variant="secondary">{tag}</Badge>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-base font-semibold text-primary-foreground"
              >
                {initialsOf(customer.name)}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold">{customer.name}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {formatMobile(customer.primaryPhone)}
                </p>
              </div>
            </div>

            {/* The two things somebody opened this screen to do, as tiles a thumb can find. */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`tel:+91${customer.primaryPhone}`}
                className="flex flex-col items-center gap-1 rounded-lg bg-muted px-2 py-3 text-xs font-semibold text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <Phone aria-hidden="true" className="size-5 text-primary" />
                {t('customers.detail.call')}
              </a>
              <a
                href={`https://wa.me/91${customer.primaryPhone}`}
                target="_blank"
                rel="noreferrer noopener"
                className="flex flex-col items-center gap-1 rounded-lg bg-muted px-2 py-3 text-xs font-semibold text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <MessageCircle aria-hidden="true" className="size-5 text-primary" />
                {t('customers.detail.whatsapp')}
              </a>
            </div>

            <dl className="flex flex-col gap-2 text-sm">
              <Field label={t('customers.field.primaryPhone')}>
                {formatMobile(customer.primaryPhone)}
              </Field>
            </dl>

            {/* Who is looking after them. Changed here in one move rather than through the whole
                edit form, because handing somebody over is the commonest edit there is. */}
            <div className="flex flex-col gap-1 text-sm">
              <label htmlFor="customer-assignee" className="text-muted-foreground">
                {t('customers.detail.assignedTo')}
              </label>
              <StaffAssigneeSelect
                id="customer-assignee"
                value={customer.assignedToUserId ?? ''}
                currentName={customer.assignedToName ?? null}
                disabled={update.isPending}
                onChange={onAssign}
              />
            </div>
          </section>

          {/* One column, not two: this pane is narrow, and a label above its value reads better
              here than the side-by-side grid the dialog's full width allowed. */}
          <section className="dossier-details flex flex-col gap-3">
            <h3 className="text-sm font-semibold">{t('customers.detail.detailsHeading')}</h3>
            <dl className="flex flex-col gap-3 text-sm">
              <Field label={t('customers.field.email')}>
                {customer.email ? (
                  <a href={`mailto:${customer.email}`} className="break-all hover:underline">
                    {customer.email}
                  </a>
                ) : null}
              </Field>
              {/* Type is shown only for a business, and the GSTIN only underneath it. On the
                  ordinary walk-in's page both rows would say nothing worth the space. */}
              {customer.kind === 'b2b' ? (
                <>
                  <Field label={t('customers.field.kind')}>{t('customers.kind.b2b')}</Field>
                  <Field label={t('customers.field.gstin')}>{customer.gstin}</Field>
                </>
              ) : null}
              {/* Shown only when given. A dash against "Birthday" on every walk-in's page
                  would be a column of nothing. */}
              {customer.dateOfBirth ? (
                <Field label={t('customers.field.dateOfBirth')}>
                  {formatDate(customer.dateOfBirth)}
                </Field>
              ) : null}
              {customer.anniversary ? (
                <Field label={t('customers.field.anniversary')}>
                  {formatDate(customer.anniversary)}
                </Field>
              ) : null}
              <Field label={t('customers.field.source')}>
                {customer.source ? t(`customers.source.${customer.source}`) : null}
              </Field>
              <Field label={t('customers.field.addressLine1')}>
                {address || customer.address.pincode
                  ? [address, customer.address.pincode].filter(Boolean).join(' — ')
                  : null}
              </Field>
              <Field label={t('customers.detail.addedOn')}>{formatDate(customer.createdAt)}</Field>
            </dl>
          </section>
        </aside>

        <section className="dossier-main flex min-w-0 flex-col gap-4">
          <div
            role="tablist"
            aria-label={t('customers.detail.tabs.label')}
            className="dossier-tabs flex flex-wrap items-center gap-1"
          >
            {tabs.map((one) => {
              const count = tabCounts[one];
              return (
                <button
                  key={one}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === one}
                  data-tour={`customer-tab-${one}`}
                  onClick={() => setTab(one)}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    activeTab === one
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`customers.detail.tabs.${one}`)}
                  {/* A zero is not shown. "Orders 0" and "Orders" say the same thing, and only
                      one of them is worth the eye stopping on. */}
                  {count ? <Badge variant="secondary">{count}</Badge> : null}
                </button>
              );
            })}
          </div>

          {activeTab === 'timeline' ? (
            timeline.isPending ? (
              <ListSkeleton rows={4} />
            ) : (
              <CustomerTimeline
                entries={timeline.data?.items ?? []}
                truncated={timeline.data?.truncated ?? false}
                onOpen={{
                  measurement: () => setTab('measurements'),
                  design: () => setTab('designs'),
                  ...(tabs.includes('quotation') ? { quotation: () => setTab('quotation') } : {}),
                }}
              />
            )
          ) : null}

          {activeTab === 'calls' ? (
            <CustomerCallsTab customerId={customer.id} customerName={customer.name} />
          ) : null}

          {activeTab === 'followUps' ? <CustomerFollowUpsTab customerId={customer.id} /> : null}

          {/* What they were quoted, which is the figure a shop is asked to honour when they come
              back. A tailor never sees this tab at all. */}
          {activeTab === 'quotation' && canSeeQuotations ? (
            <QuotationCard customerId={customer.id} customerName={customer.name} />
          ) : null}

          {activeTab === 'orders' ? <CustomerOrders customerId={customer.id} /> : null}

          {activeTab === 'measurements' ? (
            <MeasurementsPanel owner={{ kind: 'customer', customerId: customer.id }} />
          ) : null}

          {/*
            Her designs — one board for the person. A walk-in starts an empty board, which is the
            case an order-only board could not serve at all.
          */}
          {activeTab === 'designs' ? (
            <DesignBoardPanel owner={{ kind: 'customer', customerId: customer.id }} />
          ) : null}

          {/*
            THE CUSTOMER'S NOTES: a thread, each note with its author and time, that staff add to,
            edit and delete here. Under it, the profile's one free-text box (edited through the
            form).
          */}
          {activeTab === 'notes' ? (
            <div className="flex flex-col gap-6">
              <NotesThread subject={{ kind: 'customer', id: customer.id }} />

              <section className="flex flex-col items-start gap-3">
                <h3 className="text-sm font-semibold">
                  {t('customers.detail.customerNotesHeading')}
                </h3>
                <p className="whitespace-pre-line text-sm text-muted-foreground">
                  {customer.notes ?? t('customers.detail.noNotes')}
                </p>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/customers/${customer.id}/edit`}>
                    {t('customers.detail.editNotes')}
                  </Link>
                </Button>
              </section>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

/**
 * The way back to the book — carrying the search that was typed to get here.
 *
 * The list keeps what was typed in its own address (`/customers?q=…`), and the row that opened
 * this page hands it over as navigation state. Without that, walking back from a customer would
 * empty the search box, and the counter would retype a number it had already typed once.
 */
function BackLink(): ReactElement {
  const { t } = useTranslation();
  const { state } = useLocation();
  const fromSearch = (state as { fromSearch?: unknown } | null)?.fromSearch;
  const to =
    typeof fromSearch === 'string' && fromSearch
      ? `/customers?q=${encodeURIComponent(fromSearch)}`
      : '/customers';

  return (
    <Link
      to={to}
      className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
      {t('customers.backToList')}
    </Link>
  );
}

/** One figure in the left pane, read as "label … value" on a single line. */
function StatRow({
  dotClassName,
  label,
  value,
}: {
  dotClassName: string;
  label: string;
  value: string;
}): ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex items-center gap-2 text-muted-foreground">
        <span aria-hidden="true" className={cn('size-2 rounded-full', dotClassName)} />
        {label}
      </dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** One fact about the person. An em dash rather than a hidden row, so the pane keeps its shape. */
function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">
        {children ?? <span className="text-muted-foreground/60">—</span>}
      </dd>
    </div>
  );
}

export default CustomerDetailPage;

/** Two letters for the avatar: first and last name, or the first two of a single name. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  const letters = last ? `${first[0] ?? ''}${last[0] ?? ''}` : first.slice(0, 2);
  return letters.toUpperCase();
}
